import usePageTitle from '../hooks/usePageTitle';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Building2,
  MapPin,
  Plus,
  User,
  Loader,
  AlertCircle,
  Users,
  LayoutDashboard,
} from 'lucide-react';
import { clientsService, facilitiesService, usersService } from '../services';
import type { ClientSettings } from '../services/clients';
import type { Client, Facility, User as UserType } from '../types';
import AddUserModal from '../components/Forms/AddUserModal';
import AddFacilityModal from '../components/Forms/AddFacilityModal';
import ClientOverview from '../components/Overview/ClientOverview';
import { RiskBadge } from '../components/Overview';
import { useOverview } from '../components/Overview/useOverview';
import { useToast } from '../contexts/ToastContext';
import './ClientDashboard.css';

type Tab = 'overview' | 'facilities' | 'people' | 'settings';

const ClientDashboard: React.FC = () => {
  usePageTitle('Client');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isH2okayAdmin } = useAuth();
  const { startActingAs } = useImpersonation();
  const [openingClientView, setOpeningClientView] = useState(false);
  const { showSuccess, showError } = useToast();
  const [client, setClient] = useState<Client | null>(null);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [users, setUsers] = useState<UserType[]>([]);
  const overview = useOverview(id);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showAddFacilityModal, setShowAddFacilityModal] = useState(false);
  const [settings, setSettings] = useState<ClientSettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);

  const people = useMemo(
    () => (id ? users.filter((u) => u.client_id === id) : []),
    [id, users]
  );

  useEffect(() => {
    if (activeTab === 'overview') return;
    if (!id) {
      setLoading(false);
      setError('No client ID');
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        void usersService.getUsers().then(rows => { if (!cancelled) setUsers(rows); }).catch(() => {});
        const [clientRes, facilitiesRes] = await Promise.all([
          clientsService.getClientById(id),
          facilitiesService.getFacilities({ client_id: id }),
        ]);
        if (cancelled) return;
        setClient(clientRes);
        setFacilities(facilitiesRes);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load client data');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [id, activeTab]);

  useEffect(() => {
    if (!id || activeTab !== 'settings' || settings) return;

    let cancelled = false;
    setSettingsLoading(true);
    clientsService
      .getSettings(id)
      .then((loadedSettings) => {
        if (!cancelled) {
          setSettingsLoading(false);
          setSettings(loadedSettings);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          showError(
            'Unable to load client settings',
            e instanceof Error ? e.message : undefined
          );
        }
      })
      .finally(() => {
        if (!cancelled) setSettingsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab, id, settings, showError]);

  const handleFacilityClick = (facilityId: string) => {
    navigate(`/facilities/${facilityId}`);
  };

  const handleAddFacilityClick = () => {
    setShowAddFacilityModal(true);
  };

  const handleAddFacilitySuccess = async (formData: {
    name: string;
    address: string;
    description?: string;
    jurisdiction?: string;
    facility_category?: string;
    clientId: string;
    managerId: string;
    additionalStaff?: Array<{ userId: string; userName: string; role: string }>;
  }) => {
    const newFacility = await facilitiesService.createFacility({
      name: formData.name,
      jurisdiction: formData.jurisdiction || null,
      facility_category: formData.facility_category || null,
      address: formData.address,
      description: formData.description || undefined,
      client_id: formData.clientId,
      manager_id: formData.managerId
    });

    // Add additional staff assignments if any
    if (formData.additionalStaff?.length) {
      await Promise.all(
        formData.additionalStaff.map((staff, i) =>
          facilitiesService.addFacilityStaff(newFacility.id, {
            user_id: staff.userId,
            role: staff.role || undefined,
            order: i + 1,
          }).catch((err: unknown) => {
            console.error(`Failed to add staff ${staff.userName}:`, err);
          })
        )
      );
    }

    const [facilitiesRes] = await Promise.all([
      facilitiesService.getFacilities({ client_id: id! })
    ]);
    setFacilities(facilitiesRes);
    overview.retry();
    return newFacility.id;
  };

  const handleAddPersonSuccess = () => {
    usersService.getUsers().then(setUsers);
    setShowAddUserModal(false);
  };

  const handleSaveSettings = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!id || !settings) return;

    setSettingsSaving(true);
    try {
      const savedSettings = await clientsService.saveSettings(id, settings);
      setSettings(savedSettings);
      overview.retry();
      showSuccess('Client settings saved');
    } catch (e) {
      showError(
        'Unable to save client settings',
        e instanceof Error ? e.message : undefined
      );
    } finally {
      setSettingsSaving(false);
    }
  };

  if (!id) {
    return (
      <div className="client-dashboard">
        <div className="client-dashboard-error">
          <AlertCircle size={24} />
          <p>No client selected.</p>
        </div>
      </div>
    );
  }

  if (loading && activeTab !== 'overview') {
    return (
      <div className="client-dashboard">
        <div className="client-dashboard-loading">
          <Loader className="loading-spinner" size={32} />
          <p>Loading client...</p>
        </div>
      </div>
    );
  }

  if ((error || !client) && activeTab !== 'overview') {
    return (
      <div className="client-dashboard">
        <div className="client-dashboard-error">
          <AlertCircle size={24} />
          <p>{error ?? 'Client not found.'}</p>
        </div>
      </div>
    );
  }

  const previewUser = overview.data?.client?.users.find(user => user.id && user.role === 'client');
  const openClientView = async () => {
    if (!previewUser?.id) return;
    setOpeningClientView(true);
    try {
      await startActingAs(previewUser.id);
      navigate('/portal');
    } catch (e) {
      showError('Unable to open client view', e instanceof Error ? e.message : undefined);
    } finally {
      setOpeningClientView(false);
    }
  };

  const tabButtons: { key: Tab; label: string; icon?: React.ReactNode }[] = [
    { key: 'overview', label: 'Overview', icon: <LayoutDashboard size={18} /> },
    { key: 'facilities', label: 'Facilities', icon: <Building2 size={18} /> },
    { key: 'people', label: 'People', icon: <Users size={18} /> },
    { key: 'settings', label: 'Settings' }
  ];

  return (
    <div className="client-dashboard">
      <header className="overview-header client-compact-header">
        <div><div className="overview-client-contact"><h1>{overview.data?.client?.name || client?.name || 'Client'}</h1><RiskBadge risk={overview.data?.stats.worst_risk}/></div>
          <div className="overview-client-contact"><span>{overview.data?.client?.responsible_consultant || 'No responsible consultant assigned'}</span>
            <span>{overview.data?.client?.primary_contact.name}</span>
            {overview.data?.client?.primary_contact.email && <a href={`mailto:${overview.data.client.primary_contact.email}`} aria-label="Email primary contact">✉ {overview.data.client.primary_contact.email}</a>}
            {overview.data?.client?.primary_contact.phone && <a href={`tel:${overview.data.client.primary_contact.phone}`} aria-label="Call primary contact">☎ {overview.data.client.primary_contact.phone}</a>}
          </div>
        </div>
        <div className="overview-header-actions">
          {isH2okayAdmin && previewUser && <button className="btn btn-secondary btn-sm" disabled={openingClientView} onClick={openClientView}>Open client view</button>}
          <button className="btn btn-secondary btn-sm" onClick={handleAddFacilityClick}>+ Add facility</button>
        </div>
      </header>

      {/* Tab bar */}
      <div className="client-dashboard-tabs">
        {tabButtons.map(({ key, label, icon }) => (
          <button
            key={key}
            type="button"
            className={`tab-button ${activeTab === key ? 'active' : ''}`}
            onClick={() => setActiveTab(key)}
          >
            {icon}
            <span>{label}</span>
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="client-dashboard-content">
        {activeTab === 'overview' && <ClientOverview id={id} {...overview} />}

        {activeTab === 'facilities' && (
          <section className="dashboard-section">
            <div className="section-header">
              <h2>Facilities ({facilities.length})</h2>
              <button className="btn btn-primary" onClick={handleAddFacilityClick}>
                <Plus size={20} />
                Add Facility
              </button>
            </div>
            <div className="facilities-grid">
              {facilities.length === 0 ? (
                <p className="empty-state">No facilities yet. Add one to get started.</p>
              ) : (
                facilities.map((facility) => (
                  <div
                    key={facility.id}
                    className="facility-card card"
                    onClick={() => handleFacilityClick(facility.id)}
                  >
                    <div className="facility-header">
                      <div className="facility-icon">
                        <Building2 size={24} />
                      </div>
                    </div>
                    <h3>{facility.name}</h3>
                    <div className="facility-stats">
                      <div className="stat">
                        <MapPin size={16} />
                        <span>{facility.address || '—'}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
        )}

        {activeTab === 'people' && (
          <section className="dashboard-section">
            <div className="section-header">
              <h2>People ({people.length})</h2>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowAddUserModal(true)}
              >
                <Plus size={20} />
                Add Person
              </button>
            </div>
            {people.length === 0 ? (
              <p className="empty-state">No people assigned to this client yet.</p>
            ) : (
              <div className="people-table-wrap">
                <table className="people-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {people.map((user) => (
                      <tr key={user.id}>
                        <td>
                          <div className="person-name">
                            <User size={16} />
                            {user.name}
                          </div>
                        </td>
                        <td>{user.email}</td>
                        <td>
                          <span className="badge badge-default">{user.role}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {activeTab === 'settings' && (
          <section className="dashboard-section">
            <div className="section-header">
              <h2>Client Settings</h2>
            </div>
            {settingsLoading ? (
              <div className="client-settings-loading">
                <Loader className="loading-spinner" size={24} />
                <span>Loading settings...</span>
              </div>
            ) : settings ? (
              <form className="client-settings-form" onSubmit={handleSaveSettings}>
                <fieldset className="client-settings-group">
                  <legend>Notifications</legend>
                  <label className="client-settings-toggle">
                    <input
                      type="checkbox"
                      checked={settings.notifyOnInspectionComplete}
                      onChange={(event) =>
                        setSettings({
                          ...settings,
                          notifyOnInspectionComplete: event.target.checked,
                        })
                      }
                    />
                    <span>Notify on inspection completion</span>
                  </label>
                  <label className="client-settings-toggle">
                    <input
                      type="checkbox"
                      checked={settings.notifyOnHighRiskFindings}
                      onChange={(event) =>
                        setSettings({
                          ...settings,
                          notifyOnHighRiskFindings: event.target.checked,
                        })
                      }
                    />
                    <span>Notify on high-risk findings</span>
                  </label>
                  <label className="client-settings-toggle">
                    <input
                      type="checkbox"
                      disabled
                      checked={settings.weeklyDigest}
                      onChange={(event) =>
                        setSettings({ ...settings, weeklyDigest: event.target.checked })
                      }
                    />
                    <span>Send weekly digest — Coming soon</span>
                  </label>
                </fieldset>

                <fieldset className="client-settings-group client-settings-fields">
                  <legend>Primary contact</legend>
                  <label>
                    <span>Name</span>
                    <input
                      type="text"
                      value={settings.primaryContactName}
                      onChange={(event) =>
                        setSettings({ ...settings, primaryContactName: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    <span>Email</span>
                    <input
                      type="email"
                      value={settings.primaryContactEmail}
                      onChange={(event) =>
                        setSettings({ ...settings, primaryContactEmail: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    <span>Phone</span>
                    <input
                      type="tel"
                      value={settings.primaryContactPhone}
                      onChange={(event) =>
                        setSettings({ ...settings, primaryContactPhone: event.target.value })
                      }
                    />
                  </label>
                </fieldset>

                <fieldset className="client-settings-group client-settings-fields">
                  <legend>Compliance</legend>
                  <label>
                    <span>Default inspection frequency (days) — Coming soon</span>
                    <input
                      type="number"
                      min="1"
                      disabled
                      value={settings.defaultInspectionFrequencyDays}
                      onChange={(event) =>
                        setSettings({
                          ...settings,
                          defaultInspectionFrequencyDays: Number(event.target.value),
                        })
                      }
                    />
                  </label>
                  <label>
                    <span>Escalation risk threshold</span>
                    <select
                      value={settings.escalationRiskThreshold}
                      onChange={(event) =>
                        setSettings({
                          ...settings,
                          escalationRiskThreshold: event.target.value,
                        })
                      }
                    >
                      <option value="low">Low</option><option value="moderate">Moderate</option>
                      <option value="high">High</option><option value="very_high">Very High</option>
                    </select>
                  </label>
                </fieldset>

                <div className="client-settings-actions">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={settingsSaving}
                  >
                    {settingsSaving && <Loader className="loading-spinner" size={18} />}
                    {settingsSaving ? 'Saving...' : 'Save settings'}
                  </button>
                </div>
              </form>
            ) : (
              <p className="empty-state">Settings could not be loaded.</p>
            )}
          </section>
        )}
      </div>

      <AddUserModal
        isOpen={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
        clientId={id}
        onSuccess={handleAddPersonSuccess}
      />

      <AddFacilityModal
        isOpen={showAddFacilityModal}
        onClose={() => setShowAddFacilityModal(false)}
        onSuccess={handleAddFacilitySuccess}
        initialClientId={id}
        initialClientName={overview.data?.client?.name || client?.name}
      />
    </div>
  );
};

export default ClientDashboard;
